import type {
  ControlCenterAssetKind,
  ControlCenterLicenseType,
  ControlCenterMunicipalityLevel,
  ControlCenterPersonKind,
} from '../types/controlCenter'
import type {
  BoundaryShowAllLevel,
  MunicipalityLicenseStatusFilter,
  OmniAssetFilters,
  OmniJobFilters,
  OmniMunicipalityFilters,
  OmniPersonFilters,
} from '../components/controlCenter/map/controlCenterMapTypes'

const JOB_ACTIVITY = ['active', 'inactive'] as const
const PERSON_KINDS: ControlCenterPersonKind[] = ['employee', 'external']
const PERSON_CLOCKED_IN = ['yes', 'no'] as const
const ASSET_KINDS: ControlCenterAssetKind[] = ['vehicle', 'assetTracker']
const MUNICIPALITY_LEVELS: ControlCenterMunicipalityLevel[] = ['city', 'county', 'state', 'federal']
const LICENSE_TYPES: ControlCenterLicenseType[] = [
  'sos_registration',
  'business',
  'contractor',
  'electrical',
  'other',
]
const MUNICIPALITY_STATUSES: MunicipalityLicenseStatusFilter[] = ['current', 'expired', 'expiring']
const BOUNDARY_SHOW_LEVELS: BoundaryShowAllLevel[] = ['none', 'state', 'county', 'city']

export type ControlCenterFilterPreset = {
  jobFilters: OmniJobFilters
  personFilters: OmniPersonFilters
  assetFilters: OmniAssetFilters
  municipalityFilters: OmniMunicipalityFilters
  showAllJobs: boolean
  showAllPeople: boolean
  showAllAssets: boolean
  boundaryShowLevel: BoundaryShowAllLevel
}

export const EMPTY_JOB_FILTERS: OmniJobFilters = {
  states: [],
  projectTypes: [],
  statuses: [],
  activity: [],
}

export const EMPTY_PERSON_FILTERS: OmniPersonFilters = {
  kinds: [],
  clockedIn: [],
}

export const EMPTY_ASSET_FILTERS: OmniAssetFilters = {
  kinds: [],
  statuses: [],
}

export const EMPTY_MUNICIPALITY_FILTERS: OmniMunicipalityFilters = {
  states: [],
  levels: [],
  licenseTypes: [],
  statuses: [],
}

export const DEFAULT_FILTER_PRESET: ControlCenterFilterPreset = {
  jobFilters: EMPTY_JOB_FILTERS,
  personFilters: EMPTY_PERSON_FILTERS,
  assetFilters: EMPTY_ASSET_FILTERS,
  municipalityFilters: EMPTY_MUNICIPALITY_FILTERS,
  showAllJobs: false,
  showAllPeople: false,
  showAllAssets: false,
  boundaryShowLevel: 'none',
}

function uniqueStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const out: string[] = []
  const seen = new Set<string>()
  for (const item of value) {
    const s = String(item ?? '').trim()
    if (!s || seen.has(s)) continue
    seen.add(s)
    out.push(s)
  }
  return out
}

function pickAllowed<T extends string>(value: unknown, allowed: readonly T[]): T[] {
  const allowedSet = new Set<string>(allowed)
  return uniqueStrings(value).filter((s): s is T => allowedSet.has(s))
}

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
}

export function normalizeJobFilters(raw: unknown): OmniJobFilters {
  const src = asRecord(raw)
  return {
    states: uniqueStrings(src.states).map((s) => s.toUpperCase()),
    projectTypes: uniqueStrings(src.projectTypes),
    statuses: uniqueStrings(src.statuses),
    activity: pickAllowed(src.activity, JOB_ACTIVITY),
  }
}

export function normalizePersonFilters(raw: unknown): OmniPersonFilters {
  const src = asRecord(raw)
  return {
    kinds: pickAllowed(src.kinds, PERSON_KINDS),
    clockedIn: pickAllowed(src.clockedIn, PERSON_CLOCKED_IN),
  }
}

export function normalizeAssetFilters(raw: unknown): OmniAssetFilters {
  const src = asRecord(raw)
  return {
    kinds: pickAllowed(src.kinds, ASSET_KINDS),
    statuses: uniqueStrings(src.statuses),
  }
}

export function normalizeMunicipalityFilters(raw: unknown): OmniMunicipalityFilters {
  const src = asRecord(raw)
  return {
    states: uniqueStrings(src.states).map((s) => s.toUpperCase()),
    levels: pickAllowed(src.levels, MUNICIPALITY_LEVELS),
    licenseTypes: pickAllowed(src.licenseTypes, LICENSE_TYPES),
    statuses: pickAllowed(src.statuses, MUNICIPALITY_STATUSES),
  }
}

export function normalizeFilterPreset(raw: unknown): ControlCenterFilterPreset {
  const src = asRecord(raw)
  const boundaryShowLevel = BOUNDARY_SHOW_LEVELS.includes(String(src.boundaryShowLevel) as BoundaryShowAllLevel)
    ? (String(src.boundaryShowLevel) as BoundaryShowAllLevel)
    : DEFAULT_FILTER_PRESET.boundaryShowLevel

  return {
    jobFilters: normalizeJobFilters(src.jobFilters),
    personFilters: normalizePersonFilters(src.personFilters),
    assetFilters: normalizeAssetFilters(src.assetFilters),
    municipalityFilters: normalizeMunicipalityFilters(src.municipalityFilters),
    showAllJobs: src.showAllJobs === true,
    showAllPeople: src.showAllPeople === true,
    showAllAssets: src.showAllAssets === true,
    boundaryShowLevel,
  }
}
